import { Text, UnstyledButton, Code, Group, ActionIcon } from "@mantine/core";
import { IconLogout, IconBell } from "@tabler/icons-react";
import classes from "./Header.module.css";
import useAuth from "../../auth/useAuth";
import { useNavigate } from "react-router";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import { showErrorAlert } from "../../helper/alerts";
import { useLocation } from "react-router-dom";
import { ReactNode, useEffect, useRef, useState } from "react";
import { socket } from '../../api/SailsSocket';

interface Props {
  title: ReactNode; //
}

interface UnreadResp {
    count: number;
}

interface Notification {
    id: number;
    title: string;
    text: string;
    createdAt: string;
    deleted: boolean;
}

function Popover(props: {
    anchorRef: React.RefObject<HTMLButtonElement>;
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    width?: number;      // optional overrides
    offsetX?: number;
    offsetY?: number;
  }) {
    const {
      anchorRef,
      open,
      onClose,
      children,
      width  = 340,   // wider than before
      offsetX = -100,  // move ↔  (- = left, + = right)
      offsetY = 8     // move ↕
    } = props;
  
    /* close on outside click */
    useEffect(() => {
      function handle(e: MouseEvent) {
        if (
          open &&
          !anchorRef.current?.contains(e.target as Node) &&
          !(document.getElementById('notif-pop')?.contains(e.target as Node))
        ) {
          onClose();
        }
      }
      document.addEventListener('mousedown', handle);
      return () => document.removeEventListener('mousedown', handle);
    }, [open, onClose]);
  
    if (!open || !anchorRef.current) return null;
  
    const { bottom, right } = anchorRef.current.getBoundingClientRect();
  
    return (
      <div
        id="notif-pop"
        style={{
          position: "absolute",
          top: bottom + offsetY + window.scrollY,
          left: right + offsetX - width,
          width,
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 12,
          boxShadow: "0 4px 18px rgba(0,0,0,.15)",
          zIndex: 9999,
        }}
      >
        {children}
      </div>
    );
}

const Header: React.FC<Props> = ({ title }) => {
  const axiosPrivate = useAxiosPrivate();
  const location = useLocation();

  const { auth, setAuth } = useAuth();
  const user = {
    name: auth.name,
  };
  
  const plusone = () => {
    setNotificationCount(prev => prev + 1);
  }

  const nav = useNavigate();
  const logOut = () => {
    let endpoint = "";
    if (location.pathname === "/staff/dashboard")
      endpoint = "/api/staff/auth/logout";
    else endpoint = "/api/admin/auth/logout";

    axiosPrivate
      .post(endpoint)
      .then((res) => {
        setAuth({});
        nav("/");
      })
      .catch((err) => {
        showErrorAlert("Could not logout");
      });
  };

  const clearNoti = () => {
    axiosPrivate
      .get("/api/admin/admin-notifications/read-all-notifications")
      .catch((err) => {
        showErrorAlert("Could not clear");
      });

      setOpen(o => !o)
      setNotificationCount(0)
  };

  const [list, setList]   = useState<Notification[]>([]);
  const [open, setOpen]   = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const btnRef            = useRef<HTMLButtonElement>(null);

  useEffect(() => {
      console.log(socket.isConnected())
      if(socket.isConnected()) {
        socket.get('/api/admin/admin-notifications/get-data',
            (body: { data: Notification[], count: number }) => {
                setList(body.data ?? []);
                setNotificationCount(body?.count ?? 0)
            }
        );
      }
  
      const handleNew = () => {
        setNotificationCount(prev => prev + 1)
        }

      socket.on('new-notification', handleNew);
  
      return () => {
        socket.off('new-notification', handleNew);
      };
  }, []);

  return (
    <div>
      <div className={classes.mainSection}>
        <div>
          <Text className={classes.title}>{title}</Text>
        </div>
        <Group>
            <ActionIcon ref={btnRef} onClick={clearNoti} variant="transparent" color="#8a2be2" aria-label="Bell">
                <IconBell style={{ width: '100%', height: '100%' }} stroke={2} />
                {notificationCount > 0 && (
                    <span style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: 'red',
                    color: 'white',
                    borderRadius: '50%',
                    padding: '4px 6px',
                    fontSize: '10px'
                    }}>
                    {notificationCount}
                    </span>
                )}
            </ActionIcon>
            <Popover anchorRef={btnRef} open={open} onClose={() => setOpen(false)}>
                <header style={{ padding: '8px 12px', fontWeight: 600 }}>
                    Notifications
                </header>
                <hr style={{ margin: 0 }} />

                {list.length === 0 && (
                <p style={{ padding: 12, margin: 0, fontStyle: 'italic' }}>
                    No notifications
                </p>
                )}
                <ul
                    style={{
                        maxHeight: 260,
                        overflowY: "auto",
                        listStyle: "none",
                        margin: 0,
                        padding: 0,
                    }}
                    >
                    {list.map((n) => (
                        <li
                        key={n.id}
                        style={{
                            padding: 14,
                            borderBottom: "1px solid #eee",
                            cursor: "pointer",
                            background: n.deleted ? "#fafafa" : "white",
                        }}
                        onClick={() => setOpen(false)}
                        >
                        <div
                            style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            columnGap: 8,
                            alignItems: "start",
                            }}
                        >
                            <div style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{n.title}</div>
                            <div
                            style={{
                                fontWeight: 400,
                                color: "#555",
                                wordBreak: "break-word",
                                fontSize: 13,
                            }}
                            >
                            {n.text}
                            </div>
                        </div>
                        <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                            {new Date(n.createdAt).toLocaleString()}
                        </div>
                        </li>
                    ))}
                </ul>
            </Popover>
          <Code>{user.name}</Code>
          <ActionIcon onClick={logOut} variant="transparent" color="red" aria-label="Logout">
            <IconLogout style={{ width: '100%', height: '100%' }} stroke={2} />
            </ActionIcon>
        </Group>
      </div>
    </div>
  );
};

export default Header;
